import type { ClassicScheme } from '@retejs/lit-plugin';
import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { sortByIndex } from '../../../utils/sort-by-index';
import { whiteNodeStyles } from './white-node.styles';

type NodeExtraData = { width?: number; height?: number; taskIcon?: string };
const MAX_VALUE_LENGTH_SHORT = 20;
const MAX_VALUE_LENGTH_LONG = 250;

@customElement('white-node')
export class WhiteNodeElement extends LitElement {
  static get properties() {
    return {
      data: { type: Object },
      emit: { type: Function },
      styles: { type: Function },
    };
  }

  declare data: ClassicScheme['Node'] & NodeExtraData;
  declare emit: (type: string, payload: any) => void;
  declare styles?: (props: any) => any;

  static styles = [whiteNodeStyles];

  private formatValue(value: unknown): string {
    if (value === undefined) return '';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  }

  private formatValueShort(value: unknown): string {
    const full = this.formatValue(value);
    return full.length > MAX_VALUE_LENGTH_SHORT
      ? `${full.slice(0, MAX_VALUE_LENGTH_SHORT)}…`
      : full;
  }

  private formatValueLong(value: unknown): string {
    const full = this.formatValue(value);
    return full.length > MAX_VALUE_LENGTH_LONG
      ? `${full.slice(0, MAX_VALUE_LENGTH_LONG)}…`
      : full;
  }

  render() {
    const inputs = Object.entries(this.data.inputs || {});
    const outputs = Object.entries(this.data.outputs || {});
    const controls = Object.entries(this.data.controls || {});
    const { id, label, width, height, taskIcon, taskLabel } = this.data as any;

    sortByIndex(inputs);
    sortByIndex(outputs);
    sortByIndex(controls);

    if (this.data.selected) {
      this.classList.add('selected');
    } else {
      this.classList.remove('selected');
    }

    if ((this.data as any).disabled) {
      this.classList.add('disabled');
    } else {
      this.classList.remove('disabled');
    }

    if ((this.data as any).error) {
      this.classList.add('error');
    } else {
      this.classList.remove('error');
    }

    const status = ((this.data as any).status ?? '').toLowerCase();
    const statusClasses = ['pending', 'running', 'completed', 'failed'];
    for (const s of statusClasses) {
      this.classList.remove(`status-${s}`);
    }
    if (status && statusClasses.includes(status)) {
      this.classList.add(`status-${status}`);
    }

    this.dataset.testid = `node-${(this.data as any).taskType}`;
    return html`
      <style>
        :host {
          width: ${Number.isFinite(width) ? `${width}px` : 'var(--node-width)'};
          height: ${
            Number.isFinite(height) ? `${height}px` : 'var(--node-height)'
          };
        }
        ${this.styles?.(this)}
      </style>

      <div class="node-content">
        <div class="node-header">
          <div class="title-block">
            <div class="title" data-testid="title" title=${label}>
              ${label}
            </div>
            ${
              taskLabel
                ? html`<div
                    class="task-label"
                    data-testid="task-label"
                    title=${taskLabel}
                  >
                    ${taskLabel}
                  </div>`
                : null
            }
          </div>
        </div>
        ${
          taskIcon
            ? html`<div class="icon" data-testid="icon">
              ${unsafeSVG(taskIcon)}
            </div>`
            : null
        }
        ${outputs.map(([key, output]: any) =>
          output
            ? html` <div class="output" key=${key} data-testid=${`output-${key}`}>
                <div class="output-title" data-testid="output-title">
                  ${output?.label || key}
                </div>
                <div class="output-socket-container" data-testid="output-socket">
                  <rete-ref
                    .emit=${this.emit}
                    .data=${{
                      type: 'socket',
                      side: 'output',
                      key,
                      nodeId: id,
                      payload: output.socket,
                    }}
                  ></rete-ref>
                  <div class="output-value" data-testid="output-value">
                    <span
                      class="value"
                      title="${this.formatValueLong(output?.value)}"
                      >${this.formatValueShort(output?.value)}</span
                    >
                  </div>
                </div>
              </div>`
            : null
        )}
        ${inputs.map(([key, input]: any) =>
          input
            ? html` <div class="input" key=${key} data-testid=${`input-${key}`}>
                <div class="input-socket-container" data-testid="input-socket">
                  <rete-ref
                    .data=${{
                      type: 'socket',
                      side: 'input',
                      key,
                      nodeId: id,
                      payload: input.socket,
                      value: input.value,
                    }}
                    .emit=${this.emit}
                  ></rete-ref>
                  <div class="input-value" data-testid="input-value">
                    <span
                      class="value"
                      title="${this.formatValueLong(input?.value)}"
                      >${this.formatValueShort(input?.value)}</span
                    >
                  </div>
                </div>
                ${
                  input && (!input.control || !input.showControl)
                    ? html` <div class="input-title" data-testid="input-title">
                      ${input?.label || key}
                    </div>`
                    : null
                }
                ${
                  input?.control && input?.showControl
                    ? html`
                      <div class="control" data-testid="input-control">
                        <rete-ref
                          .emit=${this.emit}
                          .data="${{ type: 'control', payload: input.control }}"
                        ></rete-ref>
                      </div>
                    `
                    : null
                }
              </div>`
            : null
        )}
      </div>
      ${
        (this.data as any).error
          ? html`<div class="error-message">${(this.data as any).error}</div>`
          : null
      }
    `;
  }
}
